/**
 * Configuração do gravador de áudio Opus para o chat.
 *
 * ESTRATÉGIA (robusta, sem áudio picotado):
 *  - Preferimos SEMPRE o polyfill `opus-media-recorder` (build UMD) para
 *    gravar em `audio/ogg;codecs=opus` — formato esperado por Z-API / WAHA
 *    / Meta WABA e que renderiza waveform no WhatsApp.
 *  - Usamos o build UMD (`OpusMediaRecorder.umd.js`) em vez do CJS padrão,
 *    pois o CJS referencia `global.AudioContext` e Vite não polyfilla
 *    `global` no browser, lançando `ReferenceError: global is not defined`
 *    (issue oficial kbumsik/opus-media-recorder#76).
 *  - Caso o polyfill não carregue, caímos para o `MediaRecorder` NATIVO
 *    com Opus, garantindo que a gravação não quebre.
 *  - NÃO substituímos `window.MediaRecorder` globalmente — o hook decide
 *    qual implementação instanciar a cada gravação.
 */

// Caminhos dos arquivos WASM (copiados pelo vite-plugin-static-copy)
const OPUS_WORKER_PATH = '/opus-media-recorder/encoderWorker.umd.js';
const OGG_OPUS_WASM_PATH = '/opus-media-recorder/OggOpusEncoder.wasm';
const WEBM_OPUS_WASM_PATH = '/opus-media-recorder/WebMOpusEncoder.wasm';

// Opções do worker do OpusMediaRecorder
export interface OpusWorkerOptions {
  encoderWorkerFactory: () => Worker;
  OggOpusEncoderWasmPath: string;
  WebMOpusEncoderWasmPath: string;
}

// Cache do construtor do polyfill (carregado sob demanda)
let polyfillCtor: any | null = null;
let workerOptions: OpusWorkerOptions | null = null;

/**
 * Detecta se o MediaRecorder nativo suporta algum mimetype Opus.
 * Retorna o mimetype suportado ou null.
 */
export function detectNativeOpusMime(): string | null {
  if (typeof window === 'undefined' || typeof (window as any).MediaRecorder === 'undefined') {
    return null;
  }
  const NativeMR: any = (window as any).MediaRecorder;
  if (typeof NativeMR.isTypeSupported !== 'function') {
    return null;
  }
  // Preferimos OGG/Opus (compatível com WhatsApp); WebM/Opus serve como
  // fallback em Chrome/Edge se OGG não estiver disponível nativamente.
  const candidates = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus'];
  for (const m of candidates) {
    try {
      if (NativeMR.isTypeSupported(m)) return m;
    } catch {
      // ignora e segue testando
    }
  }
  return null;
}

/**
 * Carrega (lazy) o polyfill `opus-media-recorder` via build UMD.
 * Retorna o construtor + as opções de worker.
 */
export async function loadOpusPolyfill(): Promise<{
  ctor: any;
  options: OpusWorkerOptions;
}> {
  // Reusa instância já carregada
  if (polyfillCtor && workerOptions) {
    return { ctor: polyfillCtor, options: workerOptions };
  }

  // Importa o build UMD — não depende de `global`, compatível com Vite
  const mod: any = await import('opus-media-recorder/OpusMediaRecorder.umd.js');
  const OpusMediaRecorder =
    mod?.default ?? mod?.OpusMediaRecorder ?? (window as any).OpusMediaRecorder;

  if (typeof OpusMediaRecorder !== 'function') {
    throw new Error('OpusMediaRecorder UMD não exportou um construtor válido');
  }

  workerOptions = {
    encoderWorkerFactory: () => new Worker(OPUS_WORKER_PATH),
    OggOpusEncoderWasmPath: OGG_OPUS_WASM_PATH,
    WebMOpusEncoderWasmPath: WEBM_OPUS_WASM_PATH,
  };

  polyfillCtor = OpusMediaRecorder;
  return { ctor: polyfillCtor, options: workerOptions };
}
